import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetCatalogQuery, useLinksQuery } from '@platform/state/api';
import { useForeignRecords } from '../../tabs/related/useForeignRecords';
import type { IRelatedSectionsProps } from './IRelatedSectionsProps';
import { RelatedCard } from './RelatedCard';
import { relatedFilterOf } from './relatedFilterOf';
import { relatedGroups } from './relatedGroups';
import { workspaceTypeKeys } from './workspaceTypeKeys';

/**
 * «Пов’язані записи» на вкладці «Загальне» (прототип v4, relSecs): картка на кожну секцію визначення (модуль + підпис: CRM «Акаунти» й
 * «Контакти» — окремо, кожна зі своїм фільтром ролі `related[].filter`) — зв'язані записи й записи, що
 * посилаються на цей запис полем (помічники мандата, членство в комітетах); «+ Зв’язати» відкриває «Зіставлення» з модулем. Лише секції
 * модулів простору (прототип: `ws.modules.find(x => x.key === mk)`, інакше секції немає): типи з каталогу простору (workspaceTypeKeys);
 * поки каталог не завантажено — без секцій.
 */
export function RelatedSections({ ws, rt, record, onLink, onOpen }: IRelatedSectionsProps) {
  const { t } = useTranslation();
  const { data: catalog } = useGetCatalogQuery({ workspaceId: ws }, { skip: !ws });
  const related = useMemo(() => {
    const keys = catalog ? workspaceTypeKeys(catalog.modules) : undefined;
    return keys ? rt.related.filter((r) => keys.has(r.module)) : [];
  }, [catalog, rt.related]);
  const { data: links = [] } = useLinksQuery(record.id, { skip: !related.length || !record.id });
  const groups = useMemo(() => relatedGroups(related, links, Infinity), [related, links]);
  const foreign = useForeignRecords(ws, useMemo(() => [...links.map((l) => l.otherModule), ...related.map((r) => r.module)], [links, related]));
  if (!groups.length) return null;
  return (
    <section className="rec-section" aria-label={t('recordPage.general.related')}>
      <div className="rec-section__title">{t('recordPage.general.related')}</div>
      <div className="rec-section__grid">
        {groups.map((g) => (
          <RelatedCard key={g.key} ws={ws} rt={rt} record={record} related={g.related} filter={relatedFilterOf(g.related)} links={g.items}
            foreign={foreign} canEdit={record.permissions?.edit !== false && Boolean(record.id)} onLink={onLink} onOpen={onOpen} />
        ))}
      </div>
    </section>
  );
}
